import { useEffect, useState } from 'react';
import { ErroreRisposta, api } from '../api/client.ts';
import { MarchioT2D } from '../app/MarchioT2D.tsx';
import { requisitiMancanti } from './requisitiPassword.ts';
import stili from './Accesso.module.css';

/** L'indirizzo a cui porta il link dell'email. Lo conosce anche App. */
export const PERCORSO_RECUPERO = '/recupera-password';

/**
 * Recupero della password dimenticata.
 *
 * Due momenti sulla stessa pagina. Senza niente dopo il "#" e' la richiesta:
 * si scrive lo username o l'email e arriva un'email con il link. Con il token
 * dopo il "#" e' la scelta della nuova password.
 *
 * Il token sta nel frammento e non nella query per un motivo: il frammento
 * non viene mai mandato al server, quindi non finisce nei registri di Caddy ne'
 * in un'intestazione Referer. Dopo averlo letto lo si toglie anche
 * dall'indirizzo, cosi' non resta nella cronologia del browser.
 */
export function RecuperoPassword() {
  const [token] = useState(() => window.location.hash.replace(/^#/, ''));

  useEffect(() => {
    if (window.location.hash !== '') {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  return (
    <div className={stili.pagina}>
      {token === '' ? <RichiestaLink /> : <NuovaPassword token={token} />}
    </div>
  );
}

function Intestazione({ titolo }: { titolo: string }) {
  return (
    <div className={stili.intestazione}>
      <MarchioT2D className={stili.logo} />
      <h1 className={stili.titolo}>{titolo}</h1>
    </div>
  );
}

function RichiestaLink() {
  const [identificativo, setIdentificativo] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [inviata, setInviata] = useState(false);

  async function invia(evento: React.FormEvent) {
    evento.preventDefault();
    setErrore(null);
    setInCorso(true);
    try {
      await api.richiediRecupero(identificativo);
      setInviata(true);
    } catch (e) {
      setErrore(e instanceof ErroreRisposta ? e.message : 'Impossibile contattare il server.');
    } finally {
      setInCorso(false);
    }
  }

  if (inviata) {
    return (
      <div className={stili.riquadro}>
        <Intestazione titolo="Controlla la posta" />
        {/* Stessa risposta se l'account esiste o no: dire "non lo conosco"
            permetterebbe di scoprire chi ha un account. */}
        <p className={stili.conferma}>
          Se esiste un account con questi dati, ti abbiamo mandato un&apos;email con il link per
          scegliere una nuova password. Il link vale 60 minuti e si può usare una volta sola.
        </p>
        <p className={stili.sottotitolo}>
          Non la trovi? Guarda nella posta indesiderata. Se non arriva, chiedi a un
          amministratore di azzerarti la password.
        </p>
        <a className={stili.collegamento} href="/">
          Torna all&apos;accesso
        </a>
      </div>
    );
  }

  return (
    <form className={stili.riquadro} onSubmit={invia}>
      <Intestazione titolo="Password dimenticata" />
      <p className={stili.sottotitolo}>
        Scrivi il tuo nome utente o l&apos;indirizzo email: ti mandiamo un link per sceglierne una
        nuova.
      </p>

      <label className={stili.campo}>
        <span className={stili.etichetta}>Nome utente o email</span>
        <input
          className={stili.input}
          value={identificativo}
          onChange={(e) => setIdentificativo(e.target.value)}
          autoComplete="username"
          autoFocus
        />
      </label>

      {errore && <p className={stili.errore}>{errore}</p>}

      <button
        type="submit"
        className={stili.pulsante}
        disabled={inCorso || identificativo.trim() === ''}
      >
        {inCorso ? 'Invio in corso…' : 'Mandami il link'}
      </button>
      <a className={stili.collegamento} href="/">
        Torna all&apos;accesso
      </a>
    </form>
  );
}

function NuovaPassword({ token }: { token: string }) {
  const [nuova, setNuova] = useState('');
  const [conferma, setConferma] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [fatto, setFatto] = useState(false);

  const mancanti = requisitiMancanti(nuova);
  const diverse = conferma.length > 0 && nuova !== conferma;
  const valida = mancanti.length === 0 && nuova === conferma;

  async function invia(evento: React.FormEvent) {
    evento.preventDefault();
    setErrore(null);
    setInCorso(true);
    try {
      await api.confermaRecupero(token, nuova);
      setFatto(true);
    } catch (e) {
      setErrore(e instanceof ErroreRisposta ? e.message : 'Cambio non riuscito.');
    } finally {
      setInCorso(false);
    }
  }

  if (fatto) {
    return (
      <div className={stili.riquadro}>
        <Intestazione titolo="Password cambiata" />
        <p className={stili.conferma}>
          Fatto. Ora puoi accedere con la nuova password. Se avevi altre sessioni aperte, sono
          state chiuse.
        </p>
        <a className={stili.pulsante + ' ' + stili.pulsanteLink} href="/">
          Vai all&apos;accesso
        </a>
      </div>
    );
  }

  return (
    <form className={stili.riquadro} onSubmit={invia}>
      <Intestazione titolo="Scegli una password" />
      <p className={stili.sottotitolo}>
        Servono almeno 8 caratteri, con una maiuscola, una minuscola, un numero e un carattere
        speciale.
      </p>

      <label className={stili.campo}>
        <span className={stili.etichetta}>Nuova password</span>
        <input
          className={stili.input}
          type="password"
          value={nuova}
          onChange={(e) => setNuova(e.target.value)}
          autoComplete="new-password"
          autoFocus
        />
      </label>

      <label className={stili.campo}>
        <span className={stili.etichetta}>Ripeti la password</span>
        <input
          className={stili.input}
          type="password"
          value={conferma}
          onChange={(e) => setConferma(e.target.value)}
          autoComplete="new-password"
        />
      </label>

      {nuova.length > 0 && mancanti.length > 0 && (
        <p className={stili.errore}>Manca: {mancanti.join(', ')}.</p>
      )}
      {diverse && <p className={stili.errore}>Le due password non coincidono.</p>}
      {errore && <p className={stili.errore}>{errore}</p>}

      <button type="submit" className={stili.pulsante} disabled={!valida || inCorso}>
        {inCorso ? 'Salvataggio…' : 'Salva la password'}
      </button>
    </form>
  );
}
